import { useState, useRef, useEffect } from "react";
import { storage, db, functions } from "../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, updateDoc, serverTimestamp, arrayUnion, arrayRemove, deleteField } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { C } from "../styles";
import { Btn } from "../ui";
import { REQUIRED_SLOTS, ALL_SLOTS, MULTI_PART_ENABLED, isMultiSlot, slotLabel, validateSlotFile, slotAcceptStr, slotTypeError, slotIcon, suggestSlot } from "../slotConfig";
import { projectCode, stripPidPrefix } from "./SessionCreator";

// ── Helpers ────────────────────────────────────────────────────────────────────

function titleToKey(title) {
  return title.trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_|_$/g, "");
}

function buildRenamedName(title, sessionId, pid, slotName, version, originalName, partNumber) {
  const ext = originalName.includes(".") ? "." + originalName.split(".").pop() : "";
  const slotPart = slotName.replace(/\s+/g, "_");
  // partNumber (>1) tags parts 2..N of a multi-part slot; part 1 and all
  // single-file slots omit it so existing names are unchanged.
  const partPart = partNumber && partNumber > 1 ? `_part${partNumber}` : "";
  return `${titleToKey(title)}_${sessionId}_${pid}_${slotPart}${partPart}_v${version}${ext}`;
}

function buildStoragePath(project, sessionId, pid, title, renamedName) {
  return `projects/${project}/sessions/${sessionId}/${pid}/${titleToKey(title)}/${renamedName}`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}

function statusStyle(status) {
  if (status === "Approved")         return { color: C.success, bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.22)"  };
  if (status === "Waiting for QA")   return { color: C.blue, bg: "rgba(96,165,250,0.1)",  border: "rgba(96,165,250,0.22)"  };
  if (status === "Submitted for QA") return { color: C.blue, bg: "rgba(96,165,250,0.1)",  border: "rgba(96,165,250,0.22)"  };
  if (status === "In Progress")      return { color: "#22d3ee", bg: "rgba(34,211,238,0.1)",  border: "rgba(34,211,238,0.22)"  };
  if (status === "Rejected")         return { color: C.danger, bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.22)" };
  return { color: C.warning, bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.22)" };
}

// ── SlotCard ──────────────────────────────────────────────────────────────────

function SlotCard({ slotName, slotData, isRequired, session, pid, userName, readOnly = false }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showReplace, setShowReplace] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [moveOtherText, setMoveOtherText] = useState("");
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [typeError, setTypeError] = useState("");
  const fileInputRef = useRef();
  const replaceInputRef = useRef();

  const availableSlots = [
    ...ALL_SLOTS.filter(s => s !== "Unlabeled" && s !== slotName),
    ...Object.keys(session.slots || {}).filter(s => !ALL_SLOTS.includes(s) && s !== "Unlabeled" && s !== slotName),
  ];
  const effectiveMoveTarget = moveTarget === "__other__" ? moveOtherText.trim() : moveTarget;

  const { activeFile, history = [], additionalParts = [] } = slotData || {};
  const isEmpty = !activeFile;
  const isMulti = MULTI_PART_ENABLED && isMultiSlot(slotName);
  const partInputRef = useRef();

  const uploadFile = async (file) => {
    setShowReplace(false);
    setPendingFile(null);
    setUploading(true);
    setProgress(0);
    try {
      const version = history.length + 1;
      const renamedName = buildRenamedName(
        session.title, session.sessionId, pid, slotName, version, file.name
      );
      const storagePath = buildStoragePath(session.project, session.sessionId, pid, session.title, renamedName);
      const url = await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(ref(storage, storagePath), file);
        task.on("state_changed",
          snap => setProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
          reject,
          async () => resolve(await getDownloadURL(task.snapshot.ref))
        );
      });
      const newFile = {
        originalName: file.name,
        renamedName,
        url,
        versionNumber: version,
        sizeBytes: file.size,
        uploadedBy: { pid, name: userName },
        uploadedAt: new Date().toISOString(),
      };
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      await updateDoc(docRef, {
        [`slots.${slotName}.activeFile`]: newFile,
        [`slots.${slotName}.history`]: arrayUnion(newFile),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };
  // SlotCard is only used for named slots — Unlabeled has its own section

  // Multi-part slots: activeFile is part 1; additionalParts holds parts 2..N in
  // order. Uploading a part appends; removing splices. Part 1 keeps using the
  // existing Replace/History flow.
  const uploadPart = async (file) => {
    if (!checkType(file)) return;
    setUploading(true);
    setProgress(0);
    try {
      const version = history.length + 1;
      const partNumber = additionalParts.length + 2; // activeFile is part 1
      const renamedName = buildRenamedName(
        session.title, session.sessionId, pid, slotName, version, file.name, partNumber
      );
      const storagePath = buildStoragePath(session.project, session.sessionId, pid, session.title, renamedName);
      const url = await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(ref(storage, storagePath), file);
        task.on("state_changed",
          snap => setProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
          reject,
          async () => resolve(await getDownloadURL(task.snapshot.ref))
        );
      });
      const newFile = {
        originalName: file.name,
        renamedName,
        url,
        versionNumber: version,
        partNumber,
        sizeBytes: file.size,
        uploadedBy: { pid, name: userName },
        uploadedAt: new Date().toISOString(),
      };
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      await updateDoc(docRef, {
        [`slots.${slotName}.additionalParts`]: arrayUnion(newFile),
        [`slots.${slotName}.history`]: arrayUnion(newFile),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const removeAdditionalPart = async (index) => {
    const next = additionalParts.filter((_, i) => i !== index);
    const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
    await updateDoc(docRef, {
      [`slots.${slotName}.additionalParts`]: next,
      updatedAt: serverTimestamp(),
    });
  };

  const handlePartPick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    uploadPart(file);
  };

  const checkType = (file) => {
    if (file.size === 0) {
      setTypeError("This file is empty (0 bytes). If it lives in iCloud/Drive, download it to your device first, then re-select.");
      return false;
    }
    if (validateSlotFile(slotName, file.name)) { setTypeError(""); return true; }
    setTypeError(slotTypeError(slotName));
    return false;
  };

  const handleFilePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    if (!checkType(file)) return;
    if (!isEmpty) { setPendingFile(file); setShowReplace(true); }
    else uploadFile(file);
  };

  const handleReplacePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    if (!checkType(file)) return;
    setPendingFile(file);
    setShowReplace(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!checkType(file)) return;
    if (!isEmpty) { setPendingFile(file); setShowReplace(true); }
    else uploadFile(file);
  };

  const handleMove = async () => {
    const targetSlot = effectiveMoveTarget;
    if (!targetSlot || moving) return;
    if (!validateSlotFile(targetSlot, activeFile.originalName)) {
      setMoveError(slotTypeError(targetSlot));
      return;
    }
    setMoveError("");
    setMoving(true);
    try {
      const sourceHistory = session.slots?.[slotName]?.history || [];
      const remainingHistory = sourceHistory.filter(f => f.renamedName !== activeFile.renamedName);
      const newSourceActive = remainingHistory.length > 0 ? remainingHistory[remainingHistory.length - 1] : null;
      const targetHistory = session.slots?.[targetSlot]?.history || [];
      const version = targetHistory.length + 1;
      const renamedName = buildRenamedName(session.title, session.sessionId, activeFile.uploadedBy?.pid, targetSlot, version, activeFile.originalName);
      const movedFile = { ...activeFile, renamedName, versionNumber: version };
      const isCustomSlot = !ALL_SLOTS.includes(slotName);
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      const updates = {
        [`slots.${targetSlot}.activeFile`]: movedFile,
        [`slots.${targetSlot}.history`]: arrayUnion(movedFile),
        updatedAt: serverTimestamp(),
      };
      if (isCustomSlot && remainingHistory.length === 0) {
        updates[`slots.${slotName}`] = deleteField();
      } else {
        updates[`slots.${slotName}.activeFile`] = newSourceActive;
        updates[`slots.${slotName}.history`] = arrayRemove(activeFile);
      }
      await updateDoc(docRef, updates);
      setShowMove(false);
      setMoveTarget("");
      setMoveOtherText("");
    } catch (err) {
      alert("Move failed: " + err.message);
    } finally {
      setMoving(false);
    }
  };

  const previewName = pendingFile
    ? buildRenamedName(session.title, session.sessionId, pid, slotName, history.length + 1, pendingFile.name)
    : "";

  const st = {
    card: {
      border: dragOver
        ? "1px solid rgba(99,102,241,0.6)"
        : !isEmpty ? "1px solid rgba(16,185,129,0.22)"
        : isRequired ? "1px solid var(--border)"
        : "1px solid var(--border-subtle)",
      background: dragOver
        ? "rgba(99,102,241,0.06)"
        : !isEmpty ? "rgba(16,185,129,0.03)"
        : "rgba(255,255,255,0.015)",
    },
  };

  return (
    <div
      onDrop={readOnly ? undefined : handleDrop}
      onDragOver={readOnly ? undefined : e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={readOnly ? undefined : () => setDragOver(false)}
      style={{
        position: "relative",
        borderRadius: "12px",
        padding: "16px 18px",
        minHeight: "110px",
        transition: "all 0.2s ease",
        ...st.card,
      }}
    >
      {/* Top row: slot name + actions */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "16px" }}>{slotIcon(slotName)}</span>
          <span style={{ fontSize: "13px", fontWeight: "600", color: isEmpty ? C.text2 : C.text1 }}>
            {slotLabel(session.project, slotName)}
          </span>
          {isRequired && isEmpty && (
            <span style={{ fontSize: "10px", color: C.warning, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", padding: "1px 6px", borderRadius: "4px", fontWeight: "600" }}>
              Required
            </span>
          )}
          {!isEmpty && (
            <span style={{ fontSize: "10px", color: C.success, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)", padding: "1px 6px", borderRadius: "4px", fontWeight: "700" }}>
              v{activeFile.versionNumber}
            </span>
          )}
        </div>

        {!isEmpty && !uploading && (
          <div style={{ display: "flex", gap: "6px", position: "relative" }}>
            {!readOnly && (
              <>
                <button
                  onClick={() => replaceInputRef.current.click()}
                  style={{ fontSize: "12px", fontWeight: "600", color: "var(--og-accent)", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "6px", padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}
                >
                  Replace
                </button>

                {/* Move to… */}
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => { setShowMove(m => !m); setMoveTarget(""); setMoveOtherText(""); setMoveError(""); setShowHistory(false); }}
                    style={{ fontSize: "12px", fontWeight: "600", color: C.warning, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: "6px", padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}
                  >
                    Move to…
                  </button>
                  {showMove && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50, background: "var(--card)", border: "1px solid var(--border-2)", borderRadius: "10px", padding: "12px", minWidth: "200px", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}>
                      <div style={{ fontSize: "10px", color: C.text3, fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "10px" }}>Move to slot</div>
                      <select
                        value={moveTarget}
                        onChange={e => { setMoveTarget(e.target.value); setMoveOtherText(""); setMoveError(""); }}
                        className="filter-select"
                        style={{ width: "100%", fontSize: "12px", background: C.surface, border: "1px solid var(--border-2)", borderRadius: "6px", color: moveTarget ? C.text1 : C.text3, padding: "6px 8px", cursor: "pointer", marginBottom: "8px", boxSizing: "border-box" }}
                      >
                        <option value="" disabled>Select slot…</option>
                        {availableSlots.map(s => <option key={s} value={s}>{slotLabel(session.project, s)}</option>)}
                        <option value="__other__">Other…</option>
                      </select>
                      {moveTarget === "__other__" && (
                        <input
                          type="text"
                          value={moveOtherText}
                          onChange={e => setMoveOtherText(e.target.value)}
                          placeholder="Slot name…"
                          autoFocus
                          style={{ width: "100%", fontSize: "12px", background: C.surface, border: "1px solid var(--border-2)", borderRadius: "6px", color: C.text1, padding: "6px 8px", outline: "none", marginBottom: "8px", boxSizing: "border-box", fontFamily: "inherit" }}
                        />
                      )}
                      {moveError && (
                        <div style={{ fontSize: "11px", color: C.danger, marginBottom: "6px" }}>⚠ {moveError}</div>
                      )}
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          onClick={() => { setShowMove(false); setMoveTarget(""); setMoveOtherText(""); setMoveError(""); }}
                          style={{ flex: 1, padding: "5px", background: "transparent", border: "1px solid var(--border-2)", borderRadius: "6px", color: C.text2, fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleMove}
                          disabled={!effectiveMoveTarget || moving}
                          style={{ flex: 1, padding: "5px", background: effectiveMoveTarget ? "rgba(99,102,241,0.2)" : "var(--surface-2)", border: `1px solid ${effectiveMoveTarget ? "rgba(99,102,241,0.4)" : "var(--border)"}`, borderRadius: "6px", color: effectiveMoveTarget ? "var(--og-accent)" : C.text3, fontSize: "12px", fontWeight: "600", cursor: effectiveMoveTarget ? "pointer" : "not-allowed", fontFamily: "inherit" }}
                        >
                          {moving ? "Moving…" : "Move →"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            <button
              onClick={() => { setShowHistory(h => !h); setShowMove(false); }}
              style={{ fontSize: "12px", fontWeight: "600", color: C.text2, background: "var(--border-subtle)", border: "1px solid var(--border-2)", borderRadius: "6px", padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}
            >
              History {showHistory ? "▲" : "▾"}
            </button>

            {showHistory && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50,
                background: "var(--card)", border: "1px solid var(--border-2)",
                borderRadius: "10px", padding: "12px", minWidth: "280px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
              }}>
                <div style={{ fontSize: "10px", color: C.text3, fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "10px" }}>
                  Version History
                </div>
                {history.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "7px 0", borderBottom: i < history.length - 1 ? "1px solid var(--border-subtle)" : "none" }}>
                    <span style={{
                      fontSize: "10px", padding: "1px 6px", borderRadius: "4px", fontWeight: "700", flexShrink: 0,
                      color: i === history.length - 1 ? C.success : C.text3,
                      background: i === history.length - 1 ? "rgba(16,185,129,0.1)" : "var(--border-subtle)",
                      border: `1px solid ${i === history.length - 1 ? "rgba(16,185,129,0.2)" : "var(--border)"}`,
                    }}>
                      v{f.versionNumber}
                    </span>
                    <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: "11px", color: C.text2, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }} title={f.renamedName}>
                      {f.renamedName}
                    </a>
                    <span style={{ fontSize: "10px", color: C.text3, flexShrink: 0, whiteSpace: "nowrap" }}>
                      {formatDate(f.uploadedAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {uploading ? (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
            <span style={{ fontSize: "12px", color: C.text3 }}>Uploading…</span>
            <span style={{ fontSize: "12px", color: C.primary, fontWeight: "600" }}>{progress}%</span>
          </div>
          <div style={{ background: "var(--border-subtle)", borderRadius: "999px", height: "4px", overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: C.primary, borderRadius: "999px", transition: "width 0.2s" }} />
          </div>
        </div>
      ) : isEmpty ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: "12px", color: C.text3, lineHeight: "1.6" }}>
            {readOnly ? "No file uploaded yet" : dragOver ? "Drop to upload" : "No file yet — drop here or browse"}
          </div>
          {!readOnly && (
            <>
              <input ref={fileInputRef} type="file" accept={slotAcceptStr(slotName)} style={{ display: "none" }} onChange={handleFilePick} />
              <button
                onClick={() => fileInputRef.current.click()}
                style={{ fontSize: "12px", fontWeight: "600", color: C.text1, background: "var(--border-subtle)", border: "1px solid var(--border-2)", borderRadius: "7px", padding: "6px 14px", cursor: "pointer", flexShrink: 0, fontFamily: "inherit" }}
              >
                Upload
              </button>
            </>
          )}
        </div>
      ) : (
        <div>
          <a
            href={activeFile.url}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: "13px", color: C.text1, fontWeight: "500", textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={activeFile.renamedName}
          >
            {activeFile.renamedName}
          </a>
          <div style={{ fontSize: "11px", color: C.text3, marginTop: "4px" }}>
            {activeFile.uploadedBy?.name} · {formatDate(activeFile.uploadedAt)}
          </div>
        </div>
      )}

      {/* Multi-part slot: parts 2..N + "Add part" (part 1 = activeFile above) */}
      {isMulti && !isEmpty && !uploading && (
        <div style={{ marginTop: "10px", borderTop: "1px solid var(--border-subtle)", paddingTop: "10px" }}>
          <div style={{ fontSize: "10px", color: C.text3, fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "8px" }}>
            Parts ({additionalParts.length + 1})
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <span style={{ fontSize: "10px", color: C.text3, fontWeight: "700", flexShrink: 0 }}>1</span>
            <a href={activeFile.url} target="_blank" rel="noreferrer" style={{ fontSize: "11px", color: C.text2, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }} title={activeFile.renamedName}>
              {activeFile.renamedName}
            </a>
          </div>
          {additionalParts.map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
              <span style={{ fontSize: "10px", color: C.text3, fontWeight: "700", flexShrink: 0 }}>{i + 2}</span>
              <a href={p.url} target="_blank" rel="noreferrer" style={{ fontSize: "11px", color: C.text2, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }} title={p.renamedName}>
                {p.renamedName}
              </a>
              {!readOnly && (
                <button
                  onClick={() => removeAdditionalPart(i)}
                  title="Remove part"
                  style={{ fontSize: "11px", fontWeight: "600", color: C.danger, background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: "5px", padding: "2px 8px", cursor: "pointer", flexShrink: 0, fontFamily: "inherit" }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <>
              <input ref={partInputRef} type="file" accept={slotAcceptStr(slotName)} style={{ display: "none" }} onChange={handlePartPick} />
              <button
                onClick={() => partInputRef.current.click()}
                style={{ marginTop: "4px", fontSize: "12px", fontWeight: "600", color: "var(--og-accent)", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "6px", padding: "4px 12px", cursor: "pointer", fontFamily: "inherit" }}
              >
                + Add part
              </button>
            </>
          )}
        </div>
      )}

      {/* Type error overlay */}
      {typeError && (
        <div style={{
          position: "absolute", inset: 0, borderRadius: "12px",
          background: "rgba(10,10,20,0.96)",
          border: "1px solid rgba(248,113,113,0.35)",
          padding: "16px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
          zIndex: 10,
        }}>
          <span style={{ fontSize: "22px" }}>⚠️</span>
          <div style={{ fontSize: "13px", fontWeight: "700", color: C.danger }}>Wrong file type</div>
          <div style={{ fontSize: "12px", color: C.text3, textAlign: "center", lineHeight: "1.6" }}>{typeError}</div>
          <button
            onClick={() => setTypeError("")}
            style={{ marginTop: "4px", padding: "6px 18px", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: "7px", color: C.danger, fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit" }}
          >
            Got it
          </button>
        </div>
      )}

      {/* Hidden replace input */}
      <input ref={replaceInputRef} type="file" accept={slotAcceptStr(slotName)} style={{ display: "none" }} onChange={handleReplacePick} />

      {/* Replace confirmation overlay */}
      {showReplace && pendingFile && (
        <div style={{
          position: "absolute", inset: 0, borderRadius: "12px",
          background: "rgba(10,10,20,0.97)",
          border: "1px solid rgba(99,102,241,0.35)",
          padding: "16px",
          display: "flex", flexDirection: "column", gap: "10px",
          zIndex: 10,
        }}>
          <div style={{ fontSize: "13px", fontWeight: "700", color: C.text1 }}>
            Replace {slotLabel(session.project, slotName)}?
          </div>
          <div style={{ fontSize: "12px", color: C.text3, lineHeight: "1.6" }}>
            Creates <span style={{ color: "var(--og-accent)", fontWeight: "600" }}>v{history.length + 1}</span> — previous version is kept in history.
          </div>
          <div style={{ fontSize: "11px", color: C.text3, background: "var(--surface-2)", borderRadius: "6px", padding: "7px 10px", fontFamily: "monospace", wordBreak: "break-all", lineHeight: "1.5" }}>
            {previewName}
          </div>
          <div style={{ display: "flex", gap: "8px", marginTop: "auto" }}>
            <button
              onClick={() => { setShowReplace(false); setPendingFile(null); }}
              style={{ flex: 1, padding: "7px", background: "transparent", border: "1px solid var(--border-2)", borderRadius: "7px", color: C.text2, fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}
            >
              Cancel
            </button>
            <button
              onClick={() => uploadFile(pendingFile)}
              style={{ flex: 1, padding: "7px", background: C.primary, border: "none", borderRadius: "7px", color: "#fff", fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit" }}
            >
              Upload v{history.length + 1}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── BulkDropZone ──────────────────────────────────────────────────────────────

function BulkDropZone({ session, pid, userName }) {
  const [dragOver, setDragOver] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [otherTexts, setOtherTexts] = useState({});
  const [bulkSlotErrors, setBulkSlotErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef();

  const getEffectiveSlot = (pf, i) =>
    pf.assignedSlot === "__other__" ? (otherTexts[i] || "").trim() || "Unlabeled" : pf.assignedSlot;

  const addFiles = (files) => {
    const all = Array.from(files);
    const empty = all.filter(f => f.size === 0);
    if (empty.length) {
      alert(`Skipped ${empty.length} empty (0-byte) file(s): ${empty.map(f => f.name).join(", ")}.\n\nIf they live in iCloud/Drive, download them to your device first, then re-add.`);
    }
    const entries = all.filter(f => f.size > 0).map(f => {
      const suggested = suggestSlot(f.name);
      const assigned = validateSlotFile(suggested, f.name) ? suggested : "Unlabeled";
      return { file: f, suggestedSlot: suggested, assignedSlot: assigned };
    });
    if (entries.length) setPendingFiles(prev => [...prev, ...entries]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleUploadAll = async () => {
    if (!pendingFiles.length) return;
    setUploading(true);

    const versionTracker = {};
    for (const slotName of ALL_SLOTS) {
      versionTracker[slotName] = (session.slots?.[slotName]?.history || []).length;
    }

    const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
    for (let i = 0; i < pendingFiles.length; i++) {
      const pf = pendingFiles[i];
      const effectiveSlot = getEffectiveSlot(pf, i);

      if (!validateSlotFile(effectiveSlot, pf.file.name)) {
        setUploadProgress(prev => ({ ...prev, [i]: -1 }));
        continue;
      }
      const { file } = pf;
      const isUnlabeled = effectiveSlot === "Unlabeled";
      try {
        if (versionTracker[effectiveSlot] === undefined) {
          versionTracker[effectiveSlot] = (session.slots?.[effectiveSlot]?.history || []).length;
        }
        const version = isUnlabeled ? null : (++versionTracker[effectiveSlot]);
        const renamedName = isUnlabeled ? file.name : buildRenamedName(session.title, session.sessionId, pid, effectiveSlot, version, file.name);
        const storageFileName = isUnlabeled ? `${Date.now()}_${file.name}` : renamedName;
        const storagePath = buildStoragePath(session.project, session.sessionId, pid, session.title, storageFileName);

        const url = await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(ref(storage, storagePath), file);
          task.on("state_changed",
            snap => setUploadProgress(prev => ({ ...prev, [i]: Math.round(snap.bytesTransferred / snap.totalBytes * 100) })),
            reject,
            async () => resolve(await getDownloadURL(task.snapshot.ref))
          );
        });

        const newFile = {
          originalName: file.name,
          renamedName,
          url,
          ...(version !== null && { versionNumber: version }),
          sizeBytes: file.size,
          uploadedBy: { pid, name: userName },
          uploadedAt: new Date().toISOString(),
        };

        const updates = {
          [`slots.${effectiveSlot}.history`]: arrayUnion(newFile),
          updatedAt: serverTimestamp(),
        };
        if (!isUnlabeled) updates[`slots.${effectiveSlot}.activeFile`] = newFile;
        await updateDoc(docRef, updates);

        setUploadProgress(prev => ({ ...prev, [i]: 100 }));
      } catch (err) {
        console.error("Bulk upload failed for", file.name, err);
        setUploadProgress(prev => ({ ...prev, [i]: -1 }));
      }
    }

    setUploading(false);
    setPendingFiles([]);
    setUploadProgress({});
  };

  if (!pendingFiles.length) {
    return (
      <div
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current.click()}
        style={{
          border: dragOver ? "2px dashed rgba(99,102,241,0.55)" : "2px dashed var(--border)",
          borderRadius: "12px",
          padding: "24px",
          textAlign: "center",
          background: dragOver ? "rgba(99,102,241,0.05)" : "transparent",
          transition: "all 0.2s",
          cursor: "pointer",
        }}
      >
        <div style={{ fontSize: "24px", marginBottom: "6px", opacity: 0.35 }}>☁️</div>
        <div style={{ fontSize: "13px", color: C.text2, fontWeight: "500", marginBottom: "3px" }}>
          {dragOver ? "Drop files to add them" : "Drop multiple files here"}
        </div>
        <div style={{ fontSize: "12px", color: C.text3 }}>
          Labels suggested automatically — you can adjust before uploading
        </div>
        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>
    );
  }

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "11px 16px", background: "rgba(255,255,255,0.02)", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: "13px", fontWeight: "600", color: C.text1 }}>
          {pendingFiles.length} file{pendingFiles.length !== 1 ? "s" : ""} queued
        </span>
        {!uploading && (
          <>
            <button
              onClick={() => fileInputRef.current.click()}
              style={{ fontSize: "12px", color: "var(--og-accent)", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" }}
            >
              + Add more
            </button>
            <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
          </>
        )}
      </div>

      {/* File rows */}
      <div style={{ maxHeight: "260px", overflowY: "auto" }}>
        {pendingFiles.map((pf, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 16px", borderBottom: "1px solid var(--surface-2)" }}>
            {/* Status icon */}
            <div style={{ width: "30px", height: "30px", borderRadius: "7px", background: "var(--surface-2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "13px" }}>
              {uploading
                ? uploadProgress[i] === 100 ? <span style={{ color: C.success }}>✓</span>
                  : uploadProgress[i] === -1 ? <span style={{ color: C.danger }}>✕</span>
                  : <span style={{ fontSize: "10px", color: C.primary, fontWeight: "700" }}>{uploadProgress[i] || 0}%</span>
                : slotIcon(pf.assignedSlot)
              }
            </div>

            {/* Filename */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "13px", color: C.text1, fontWeight: "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {pf.file.name}
              </div>
              {bulkSlotErrors[i]
                ? <div style={{ fontSize: "10px", color: C.danger }}>⚠ {bulkSlotErrors[i]}</div>
                : pf.suggestedSlot !== pf.assignedSlot && pf.assignedSlot !== "__other__" && (
                    <div style={{ fontSize: "10px", color: C.text3 }}>Suggested: {pf.suggestedSlot}</div>
                  )
              }
            </div>

            {/* Slot selector */}
            <select
              value={pf.assignedSlot}
              onChange={e => {
                const val = e.target.value;
                setPendingFiles(prev => prev.map((x, idx) => idx === i ? { ...x, assignedSlot: val } : x));
                if (val !== "__other__") setOtherTexts(prev => { const n = { ...prev }; delete n[i]; return n; });
                if (val !== "__other__" && val !== "Unlabeled" && !validateSlotFile(val, pf.file.name)) {
                  setBulkSlotErrors(prev => ({ ...prev, [i]: slotTypeError(val) }));
                } else {
                  setBulkSlotErrors(prev => { const n = { ...prev }; delete n[i]; return n; });
                }
              }}
              disabled={uploading}
              className="filter-select"
              style={{ fontSize: "12px", background: C.surface, border: `1px solid ${bulkSlotErrors[i] ? "rgba(248,113,113,0.4)" : "var(--border-2)"}`, borderRadius: "6px", color: C.text1, padding: "4px 8px", cursor: "pointer" }}
            >
              {ALL_SLOTS.map(s => <option key={s} value={s}>{slotLabel(session.project, s)}</option>)}
              <option value="__other__">Other…</option>
            </select>
            {pf.assignedSlot === "__other__" && (
              <input
                type="text"
                value={otherTexts[i] || ""}
                onChange={e => setOtherTexts(prev => ({ ...prev, [i]: e.target.value }))}
                placeholder="File type…"
                disabled={uploading}
                style={{ fontSize: "12px", background: C.surface, border: "1px solid var(--border-2)", borderRadius: "6px", color: C.text1, padding: "4px 8px", outline: "none", width: "110px", flexShrink: 0, fontFamily: "inherit" }}
              />
            )}

            {/* Remove */}
            {!uploading && (
              <button
                onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}
                style={{ background: "none", border: "none", color: C.text3, cursor: "pointer", fontSize: "16px", padding: "0", lineHeight: 1, flexShrink: 0 }}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: "11px 16px", background: "rgba(255,255,255,0.02)", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: "8px" }}>
        {!uploading && (
          <button
            onClick={() => setPendingFiles([])}
            style={{ padding: "7px 14px", background: "transparent", border: "1px solid var(--border-2)", borderRadius: "7px", color: C.text2, fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}
          >
            Clear all
          </button>
        )}
        <button
          onClick={handleUploadAll}
          disabled={uploading}
          style={{ padding: "7px 18px", background: C.primary, border: "none", borderRadius: "7px", color: "#fff", fontSize: "12px", fontWeight: "600", cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.75 : 1, fontFamily: "inherit" }}
        >
          {uploading ? "Uploading…" : `Upload All (${pendingFiles.length})`}
        </button>
      </div>
    </div>
  );
}

// ── UnlabeledSection ──────────────────────────────────────────────────────────

function UnlabeledSection({ session, readOnly = false }) {
  const [targetSlots, setTargetSlots] = useState({});
  const [otherTexts, setOtherTexts] = useState({});
  const [assigning, setAssigning] = useState(null);
  const [trashing, setTrashing] = useState(null);
  const [assignErrors, setAssignErrors] = useState({});

  const effectiveSlot = (idx) => {
    const t = targetSlots[idx];
    return t === "__other__" ? (otherTexts[idx] || "").trim() : (t || "");
  };

  const files = session.slots?.Unlabeled?.history || [];

  const handleTrash = async (fileEntry, idx) => {
    if (trashing !== null) return;
    setTrashing(idx);
    try {
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      await updateDoc(docRef, {
        "slots.Unlabeled.history": arrayRemove(fileEntry),
        "trash": arrayUnion({ ...fileEntry, trashedAt: new Date().toISOString() }),
        updatedAt: serverTimestamp(),
      });
      setTargetSlots(prev => { const n = { ...prev }; delete n[idx]; return n; });
    } catch (err) {
      alert("Trash failed: " + err.message);
    } finally {
      setTrashing(null);
    }
  };

  const handleAssign = async (fileEntry, targetSlot, idx) => {
    if (!targetSlot || assigning !== null) return;
    if (!validateSlotFile(targetSlot, fileEntry.originalName)) {
      setAssignErrors(prev => ({ ...prev, [idx]: slotTypeError(targetSlot) }));
      return;
    }
    setAssignErrors(prev => { const n = { ...prev }; delete n[idx]; return n; });
    setAssigning(idx);
    try {
      const existingHistory = session.slots?.[targetSlot]?.history || [];
      const version = existingHistory.length + 1;
      const renamedName = buildRenamedName(
        session.title, session.sessionId,
        fileEntry.uploadedBy?.pid,
        targetSlot, version, fileEntry.originalName
      );
      const assignedFile = { ...fileEntry, renamedName, versionNumber: version };
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      await updateDoc(docRef, {
        "slots.Unlabeled.history": arrayRemove(fileEntry),
        [`slots.${targetSlot}.activeFile`]: assignedFile,
        [`slots.${targetSlot}.history`]: arrayUnion(assignedFile),
        updatedAt: serverTimestamp(),
      });
      // clear that file's target selection
      setTargetSlots(prev => { const n = { ...prev }; delete n[idx]; return n; });
    } catch (err) {
      alert("Assign failed: " + err.message);
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div>
      {/* Sub-header */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
        <span style={{ fontSize: "11px", fontWeight: "600", color: C.text3, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          📎 Unlabeled Files
        </span>
        {files.length > 0 && (
          <span style={{ fontSize: "11px", color: C.text3, background: "var(--border-subtle)", border: "1px solid var(--border-2)", padding: "1px 8px", borderRadius: "4px", fontWeight: "600" }}>
            {files.length}
          </span>
        )}
        <span style={{ fontSize: "11px", color: C.text3, marginLeft: "4px" }}>— assign a slot when ready</span>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div style={{ border: "1px solid var(--border)", borderRadius: "10px", overflow: "hidden", marginBottom: "10px" }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", borderBottom: i < files.length - 1 ? "1px solid var(--surface-2)" : "none", flexWrap: "wrap" }}>
              <span style={{ fontSize: "14px", flexShrink: 0 }}>📎</span>
              <div style={{ flex: 1, minWidth: "120px" }}>
                <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: "13px", color: C.text1, fontWeight: "500", textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.originalName}
                </a>
                <div style={{ fontSize: "11px", color: C.text3, marginTop: "2px" }}>
                  {f.uploadedBy?.name} · {formatDate(f.uploadedAt)}
                </div>
              </div>

              {/* Slot picker + assign — hidden after submission */}
              {!readOnly && (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end", flexShrink: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <select
                      value={targetSlots[i] || ""}
                      onChange={e => {
                        const val = e.target.value;
                        setTargetSlots(prev => ({ ...prev, [i]: val }));
                        if (val !== "__other__") setOtherTexts(prev => { const n = { ...prev }; delete n[i]; return n; });
                        if (val !== "__other__" && !validateSlotFile(val, f.originalName)) {
                          setAssignErrors(prev => ({ ...prev, [i]: slotTypeError(val) }));
                        } else {
                          setAssignErrors(prev => { const n = { ...prev }; delete n[i]; return n; });
                        }
                      }}
                      disabled={assigning !== null || trashing !== null}
                      className="filter-select"
                      style={{ fontSize: "12px", background: C.surface, border: `1px solid ${assignErrors[i] ? "rgba(248,113,113,0.4)" : "var(--border-2)"}`, borderRadius: "6px", color: targetSlots[i] ? C.text1 : C.text3, padding: "5px 8px", cursor: "pointer", flexShrink: 0 }}
                    >
                      <option value="" disabled>Assign to…</option>
                      {ALL_SLOTS.filter(s => s !== "Unlabeled").map(s => <option key={s} value={s}>{slotLabel(session.project, s)}</option>)}
                      <option value="__other__">Other…</option>
                    </select>
                    {targetSlots[i] === "__other__" && (
                      <input
                        type="text"
                        value={otherTexts[i] || ""}
                        onChange={e => setOtherTexts(prev => ({ ...prev, [i]: e.target.value }))}
                        placeholder="File type…"
                        disabled={assigning !== null || trashing !== null}
                        style={{ fontSize: "12px", background: C.surface, border: "1px solid var(--border-2)", borderRadius: "6px", color: C.text1, padding: "5px 8px", outline: "none", width: "110px", flexShrink: 0, fontFamily: "inherit" }}
                      />
                    )}
                    <button
                      onClick={() => handleAssign(f, effectiveSlot(i), i)}
                      disabled={!effectiveSlot(i) || assigning !== null || trashing !== null}
                      style={{
                        padding: "5px 12px", borderRadius: "6px", border: "none",
                        background: effectiveSlot(i) ? "rgba(99,102,241,0.15)" : "var(--surface-2)",
                        color: effectiveSlot(i) ? "var(--og-accent)" : C.text3,
                        fontSize: "12px", fontWeight: "600", cursor: effectiveSlot(i) ? "pointer" : "not-allowed",
                        flexShrink: 0, fontFamily: "inherit", whiteSpace: "nowrap",
                      }}
                    >
                      {assigning === i ? "Assigning…" : "Assign →"}
                    </button>
                    <button
                      onClick={() => handleTrash(f, i)}
                      disabled={trashing !== null || assigning !== null}
                      title="Move to trash"
                      style={{ background: "none", border: "none", color: trashing === i ? C.text3 : C.danger, cursor: trashing !== null ? "not-allowed" : "pointer", fontSize: "15px", padding: "0 2px", lineHeight: 1, flexShrink: 0, opacity: trashing !== null && trashing !== i ? 0.4 : 1 }}
                    >
                      {trashing === i ? "…" : "🗑"}
                    </button>
                  </div>
                  {assignErrors[i] && (
                    <div style={{ fontSize: "11px", color: C.danger }}>⚠ {assignErrors[i]}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

// ── FileTable ─────────────────────────────────────────────────────────────────

const TH_STYLE = { padding: "10px 16px", textAlign: "left", fontSize: "11px", fontWeight: "600", color: C.text3, textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap" };
const TD_STYLE = { padding: "12px 16px", borderBottom: "1px solid var(--surface-2)" };

function FileTable({ session, readOnly = false }) {
  const [reassigning, setReassigning] = useState(null);
  const [reassignErrors, setReassignErrors] = useState({});
  const [pendingOther, setPendingOther] = useState({});
  const [assignTargets, setAssignTargets] = useState({});
  const [assignOtherTexts, setAssignOtherTexts] = useState({});
  const [assigningIdx, setAssigningIdx] = useState(null);
  const [trashingIdx, setTrashingIdx] = useState(null);
  const [unlabeledAssignErrors, setUnlabeledAssignErrors] = useState({});

  const handleUnlabeledTrash = async (fileEntry, idx) => {
    if (trashingIdx !== null) return;
    setTrashingIdx(idx);
    try {
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      await updateDoc(docRef, {
        "slots.Unlabeled.history": arrayRemove(fileEntry),
        "trash": arrayUnion({ ...fileEntry, trashedAt: new Date().toISOString() }),
        updatedAt: serverTimestamp(),
      });
      setAssignTargets(prev => { const n = { ...prev }; delete n[idx]; return n; });
      setAssignOtherTexts(prev => { const n = { ...prev }; delete n[idx]; return n; });
    } catch (err) {
      alert("Trash failed: " + err.message);
    } finally {
      setTrashingIdx(null);
    }
  };

  const getEffectiveTarget = (idx) => {
    const t = assignTargets[idx];
    return t === "__other__" ? (assignOtherTexts[idx] || "").trim() : (t || "");
  };

  const handleUnlabeledAssign = async (fileEntry, idx) => {
    const targetSlot = getEffectiveTarget(idx);
    if (!targetSlot || assigningIdx !== null) return;
    if (!validateSlotFile(targetSlot, fileEntry.originalName)) {
      setUnlabeledAssignErrors(prev => ({ ...prev, [idx]: slotTypeError(targetSlot) }));
      return;
    }
    setUnlabeledAssignErrors(prev => { const n = { ...prev }; delete n[idx]; return n; });
    setAssigningIdx(idx);
    try {
      const existingHistory = session.slots?.[targetSlot]?.history || [];
      const version = existingHistory.length + 1;
      const renamedName = buildRenamedName(session.title, session.sessionId, fileEntry.uploadedBy?.pid, targetSlot, version, fileEntry.originalName);
      const assignedFile = { ...fileEntry, renamedName, versionNumber: version };
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      await updateDoc(docRef, {
        "slots.Unlabeled.history": arrayRemove(fileEntry),
        [`slots.${targetSlot}.activeFile`]: assignedFile,
        [`slots.${targetSlot}.history`]: arrayUnion(assignedFile),
        updatedAt: serverTimestamp(),
      });
      setAssignTargets(prev => { const n = { ...prev }; delete n[idx]; return n; });
      setAssignOtherTexts(prev => { const n = { ...prev }; delete n[idx]; return n; });
    } catch (err) {
      alert("Assign failed: " + err.message);
    } finally {
      setAssigningIdx(null);
    }
  };

  const customSlots = Object.keys(session.slots || {}).filter(s => !ALL_SLOTS.includes(s) && s !== "Unlabeled");
  const allDisplaySlots = [...ALL_SLOTS.filter(s => s !== "Unlabeled"), ...customSlots];
  const rows = allDisplaySlots
    .map(slot => ({ slot, file: session.slots?.[slot]?.activeFile }))
    .filter(r => r.file);
  const unlabeledFiles = session.slots?.Unlabeled?.history || [];

  if (!rows.length && !unlabeledFiles.length) {
    return (
      <div style={{ textAlign: "center", padding: "48px 24px", color: C.text3, fontSize: "13px", border: "1px solid var(--border-subtle)", borderRadius: "12px" }}>
        No files uploaded yet — use slot cards or bulk upload below
      </div>
    );
  }

  const handleReassign = async (currentSlot, newSlot, file) => {
    if (currentSlot === newSlot || reassigning) return;
    if (!validateSlotFile(newSlot, file.originalName)) {
      setReassignErrors(prev => ({ ...prev, [currentSlot]: slotTypeError(newSlot) }));
      return;
    }
    setReassignErrors(prev => { const n = { ...prev }; delete n[currentSlot]; return n; });
    setReassigning(currentSlot);
    try {
      // After removing the moved file, fall back to the previous version if one exists
      const sourceHistory = session.slots?.[currentSlot]?.history || [];
      const remainingHistory = sourceHistory.filter(f => f.renamedName !== file.renamedName);
      const newSourceActive = remainingHistory.length > 0
        ? remainingHistory[remainingHistory.length - 1]
        : null;

      // Version in target slot = next after whatever is already there
      const targetHistory = session.slots?.[newSlot]?.history || [];
      const version = targetHistory.length + 1;
      const renamedName = buildRenamedName(session.title, session.sessionId, file.uploadedBy?.pid, newSlot, version, file.originalName);
      const movedFile = { ...file, renamedName, versionNumber: version };

      const isCustomSlot = !ALL_SLOTS.includes(currentSlot);
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      const updates = {
        [`slots.${newSlot}.activeFile`]: movedFile,
        [`slots.${newSlot}.history`]: arrayUnion(movedFile),
        updatedAt: serverTimestamp(),
      };
      if (isCustomSlot && remainingHistory.length === 0) {
        updates[`slots.${currentSlot}`] = deleteField();
      } else {
        updates[`slots.${currentSlot}.activeFile`] = newSourceActive;
        updates[`slots.${currentSlot}.history`] = arrayRemove(file);
      }
      await updateDoc(docRef, updates);
    } catch (err) {
      alert("Reassign failed: " + err.message);
    } finally {
      setReassigning(null);
    }
  };

  return (
    <div>
      {rows.length > 0 && (
        <div style={{ border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden", marginBottom: unlabeledFiles.length ? "24px" : 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border-subtle)", background: "rgba(255,255,255,0.02)" }}>
                <th style={TH_STYLE}>Slot</th>
                <th style={TH_STYLE}>File</th>
                <th style={TH_STYLE}>Ver</th>
                <th style={TH_STYLE}>Uploaded By</th>
                <th style={TH_STYLE}>Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ slot, file }) => (
                <tr key={slot} className="table-row">
                  <td style={TD_STYLE}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <select
                        value={pendingOther[slot] !== undefined ? "__other__" : slot}
                        onChange={e => {
                          if (e.target.value === "__other__") {
                            setPendingOther(prev => ({ ...prev, [slot]: "" }));
                          } else {
                            setPendingOther(prev => { const n = { ...prev }; delete n[slot]; return n; });
                            handleReassign(slot, e.target.value, file);
                          }
                        }}
                        disabled={!!reassigning}
                        className="filter-select"
                        style={{ fontSize: "12px", background: C.surface, border: "1px solid var(--border-2)", borderRadius: "6px", color: C.text1, padding: "4px 8px", cursor: "pointer" }}
                      >
                        {allDisplaySlots.map(s => <option key={s} value={s}>{slotLabel(session.project, s)}</option>)}
                        <option value="__other__">Other…</option>
                      </select>
                      {pendingOther[slot] !== undefined && (
                        <>
                          <input
                            type="text"
                            value={pendingOther[slot]}
                            onChange={e => setPendingOther(prev => ({ ...prev, [slot]: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === "Enter" && pendingOther[slot].trim()) {
                                const custom = pendingOther[slot].trim();
                                setPendingOther(prev => { const n = { ...prev }; delete n[slot]; return n; });
                                handleReassign(slot, custom, file);
                              }
                              if (e.key === "Escape") setPendingOther(prev => { const n = { ...prev }; delete n[slot]; return n; });
                            }}
                            placeholder="File type…"
                            autoFocus
                            style={{ fontSize: "12px", background: C.surface, border: "1px solid var(--border-2)", borderRadius: "6px", color: C.text1, padding: "4px 8px", outline: "none", width: "100px", fontFamily: "inherit" }}
                          />
                          <button
                            onClick={() => {
                              if (!pendingOther[slot].trim()) return;
                              const custom = pendingOther[slot].trim();
                              setPendingOther(prev => { const n = { ...prev }; delete n[slot]; return n; });
                              handleReassign(slot, custom, file);
                            }}
                            style={{ fontSize: "13px", color: "var(--og-accent)", background: "transparent", border: "none", cursor: "pointer", padding: "0 2px" }}
                          >✓</button>
                        </>
                      )}
                    </div>
                    {reassignErrors[slot] && (
                      <div style={{ fontSize: "11px", color: C.danger, marginTop: "4px" }}>⚠ {reassignErrors[slot]}</div>
                    )}
                  </td>
                  <td style={TD_STYLE}>
                    <a href={file.url} target="_blank" rel="noreferrer" style={{ fontSize: "13px", color: C.text1, fontWeight: "500", textDecoration: "none", display: "block", maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={file.renamedName}>
                      {file.renamedName}
                    </a>
                    <div style={{ fontSize: "11px", color: C.text3, marginTop: "2px" }}>{file.originalName}</div>
                  </td>
                  <td style={TD_STYLE}>
                    <span style={{ fontSize: "11px", color: C.success, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)", padding: "2px 7px", borderRadius: "4px", fontWeight: "700" }}>
                      v{file.versionNumber}
                    </span>
                  </td>
                  <td style={{ ...TD_STYLE, fontSize: "13px", color: C.text2 }}>{file.uploadedBy?.name}</td>
                  <td style={{ ...TD_STYLE, fontSize: "12px", color: C.text3, whiteSpace: "nowrap" }}>{formatDate(file.uploadedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Unlabeled sub-section */}
      {unlabeledFiles.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600", color: C.text3, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              📎 Unlabeled Files
            </span>
            <span style={{ fontSize: "11px", color: C.text3, background: "var(--border-subtle)", border: "1px solid var(--border-2)", padding: "1px 8px", borderRadius: "4px", fontWeight: "600" }}>
              {unlabeledFiles.length}
            </span>
          </div>
          <div style={{ border: "1px solid var(--border)", borderRadius: "10px", overflow: "hidden" }}>
            {unlabeledFiles.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", borderBottom: i < unlabeledFiles.length - 1 ? "1px solid var(--surface-2)" : "none", flexWrap: "wrap" }}>
                <span style={{ fontSize: "14px" }}>📎</span>
                <div style={{ flex: 1, minWidth: "120px" }}>
                  <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: "13px", color: C.text1, fontWeight: "500", textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.originalName}
                  </a>
                  <div style={{ fontSize: "11px", color: C.text3, marginTop: "2px" }}>{f.uploadedBy?.name} · {formatDate(f.uploadedAt)}</div>
                </div>
                {!readOnly && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end", flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <select
                        value={assignTargets[i] || ""}
                        onChange={e => {
                          const val = e.target.value;
                          setAssignTargets(prev => ({ ...prev, [i]: val }));
                          if (val !== "__other__") setAssignOtherTexts(prev => { const n = { ...prev }; delete n[i]; return n; });
                          if (val !== "__other__" && !validateSlotFile(val, f.originalName)) {
                            setUnlabeledAssignErrors(prev => ({ ...prev, [i]: slotTypeError(val) }));
                          } else {
                            setUnlabeledAssignErrors(prev => { const n = { ...prev }; delete n[i]; return n; });
                          }
                        }}
                        disabled={assigningIdx !== null || trashingIdx !== null}
                        className="filter-select"
                        style={{ fontSize: "12px", background: C.surface, border: `1px solid ${unlabeledAssignErrors[i] ? "rgba(248,113,113,0.4)" : "var(--border-2)"}`, borderRadius: "6px", color: assignTargets[i] ? C.text1 : C.text3, padding: "5px 8px", cursor: "pointer", flexShrink: 0 }}
                      >
                        <option value="" disabled>Assign to…</option>
                        {ALL_SLOTS.filter(s => s !== "Unlabeled").map(s => <option key={s} value={s}>{slotLabel(session.project, s)}</option>)}
                        <option value="__other__">Other…</option>
                      </select>
                      {assignTargets[i] === "__other__" && (
                        <input
                          type="text"
                          value={assignOtherTexts[i] || ""}
                          onChange={e => setAssignOtherTexts(prev => ({ ...prev, [i]: e.target.value }))}
                          placeholder="File type…"
                          disabled={assigningIdx !== null || trashingIdx !== null}
                          style={{ fontSize: "12px", background: C.surface, border: "1px solid var(--border-2)", borderRadius: "6px", color: C.text1, padding: "5px 8px", outline: "none", width: "110px", flexShrink: 0, fontFamily: "inherit" }}
                        />
                      )}
                      <button
                        onClick={() => handleUnlabeledAssign(f, i)}
                        disabled={!getEffectiveTarget(i) || assigningIdx !== null || trashingIdx !== null}
                        style={{
                          padding: "5px 12px", borderRadius: "6px", border: "none",
                          background: getEffectiveTarget(i) ? "rgba(99,102,241,0.15)" : "var(--surface-2)",
                          color: getEffectiveTarget(i) ? "var(--og-accent)" : C.text3,
                          fontSize: "12px", fontWeight: "600", cursor: getEffectiveTarget(i) ? "pointer" : "not-allowed",
                          flexShrink: 0, fontFamily: "inherit", whiteSpace: "nowrap",
                        }}
                      >
                        {assigningIdx === i ? "Assigning…" : "Assign →"}
                      </button>
                      <button
                        onClick={() => handleUnlabeledTrash(f, i)}
                        disabled={trashingIdx !== null || assigningIdx !== null}
                        title="Move to trash"
                        style={{ background: "none", border: "none", color: trashingIdx === i ? C.text3 : C.danger, cursor: trashingIdx !== null ? "not-allowed" : "pointer", fontSize: "15px", padding: "0 2px", lineHeight: 1, flexShrink: 0, opacity: trashingIdx !== null && trashingIdx !== i ? 0.4 : 1 }}
                      >
                        {trashingIdx === i ? "…" : "🗑"}
                      </button>
                    </div>
                    {unlabeledAssignErrors[i] && (
                      <div style={{ fontSize: "11px", color: C.danger }}>⚠ {unlabeledAssignErrors[i]}</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── AddPartnerBar ─────────────────────────────────────────────────────────────

function AddPartnerBar({ session }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const acceptedPids = session.partnerPids ?? [];
  const pendingPids = session.pendingPartnerPids ?? [];
  const allExistingPids = [...new Set([...acceptedPids, ...pendingPids])];

  const handleAdd = async () => {
    const trimmed = value.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setError("");
    try {
      const validatePID = httpsCallable(functions, "validatePID");
      const { data } = await validatePID({ pid: trimmed });
      if (data.pid === session.creatorPid) {
        setError("That's the session creator — can't add as partner.");
        return;
      }
      if (allExistingPids.includes(data.pid)) {
        const isPending = pendingPids.includes(data.pid);
        setError(`PID ${data.pid} is already ${isPending ? "invited (pending)" : "a partner"} on this session.`);
        return;
      }
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      await updateDoc(docRef, {
        pendingPartnerPids: arrayUnion(data.pid),
        sharedWith: arrayUnion(data.pid),
        updatedAt: serverTimestamp(),
      });
      setOpen(false);
      setValue("");
    } catch (err) {
      setError(err.message || "PID not found.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => { setOpen(false); setValue(""); setError(""); };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ fontSize: "12px", color: "#a78bfa", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: "6px", padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: "600" }}
      >
        + Add Partner
      </button>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <input
          autoFocus
          type="text"
          placeholder="Partner PID"
          value={value}
          onChange={e => { setValue(e.target.value); setError(""); }}
          onKeyDown={e => { if (e.key === "Enter") handleAdd(); if (e.key === "Escape") handleClose(); }}
          style={{ fontSize: "12px", background: "var(--border-subtle)", border: `1px solid ${error ? "rgba(248,113,113,0.4)" : "rgba(167,139,250,0.3)"}`, borderRadius: "6px", padding: "4px 10px", color: C.text1, outline: "none", width: "110px", fontFamily: "inherit" }}
        />
        <button
          onClick={handleAdd}
          disabled={!value.trim() || saving}
          style={{ fontSize: "12px", fontWeight: "600", color: "#a78bfa", background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: "6px", padding: "4px 10px", cursor: value.trim() ? "pointer" : "not-allowed", fontFamily: "inherit", whiteSpace: "nowrap" }}
        >
          {saving ? "Checking…" : "Add →"}
        </button>
        <button
          onClick={handleClose}
          style={{ fontSize: "12px", color: C.text3, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}
        >
          Cancel
        </button>
      </div>
      {error && <div style={{ fontSize: "11px", color: C.danger }}>{error}</div>}
    </div>
  );
}

// ── Main SessionWorkspace ─────────────────────────────────────────────────────

function SessionWorkspace({ session, pid, userName, onSubmitForQA, onBack, onRequestDelete, viewingAs = false }) {
  const [submitting, setSubmitting] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [viewMode, setViewMode] = useState("slots");
  // Local-state copy of submissionNumber so creators can edit without it
  // being clobbered every time the Firestore listener fires. Synced back
  // when the user opens a different session.
  const [subNumDraft, setSubNumDraft] = useState("");
  const [savingSubNum, setSavingSubNum] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  // Editable co-creator PID (Draft, pre-submission only).
  const [coCreatorDraft, setCoCreatorDraft] = useState("");
  const [savingCoCreator, setSavingCoCreator] = useState(false);
  const [coCreatorError, setCoCreatorError] = useState("");
  // Only resync when we open a different session — otherwise the listener
  // ticking on each upload would clobber whatever the user is typing.
  useEffect(() => {
    setSubNumDraft(session?.submissionNumber != null ? String(session.submissionNumber) : "");
    setCoCreatorDraft(session?.secondCreatorPid ? String(session.secondCreatorPid) : "");
    setCoCreatorError("");
  }, [session?.sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!session) {
    return (
      <div style={{ textAlign: "center", padding: "80px 24px", color: C.text3, fontSize: "14px" }}>
        Loading session…
      </div>
    );
  }

  const acceptedPartnerPids = session.partnerPids ?? [];
  const pendingPartnerPids = session.pendingPartnerPids ?? [];
  const allPartnerPids = [...new Set([...acceptedPartnerPids, ...pendingPartnerPids])];
  const isCreator = pid === session.creatorPid;
  const isAcceptedPartner = acceptedPartnerPids.includes(pid);
  // viewingAs = an admin inspecting this user's view read-only — never allow
  // any mutation while impersonating, regardless of creator/partner role.
  const canEdit = (isCreator || isAcceptedPartner) && session.status === "Draft" && !viewingAs;

  // Progress always tracks creator's required slots
  const requiredFilled = REQUIRED_SLOTS.filter(s => session.slots?.[s]?.activeFile);
  const missingRequired = REQUIRED_SLOTS.filter(s => !session.slots?.[s]?.activeFile);
  const allRequiredFilled = missingRequired.length === 0;
  // Creator OR an accepted partner can submit — anyone collaborating on
  // the session should be able to push it to QA once everything's uploaded.
  const canSubmit = (isCreator || isAcceptedPartner) && !viewingAs;
  const canSubmitQA = canSubmit && allRequiredFilled && session.status === "Draft";
  const alreadySubmitted = session.status !== "Draft";
  // Reopen: creator or accepted partner can pull a submitted folder back to
  // Draft to add/replace files — allowed any time it isn't yet Approved.
  const canReopen = canSubmit && alreadySubmitted && session.status !== "Approved";
  const ss = statusStyle(session.status);

  const handleSubmitForQA = async () => {
    setSubmitting(true);
    try { await onSubmitForQA(); }
    finally { setSubmitting(false); }
  };

  // Reopen a submitted folder back to Draft so files can be added/replaced.
  // Keeps the same submission ID + Storage folder; re-submitting later reuses
  // the existing TBD task (no new task is created).
  const handleReopen = async () => {
    if (reopening) return;
    setReopening(true);
    try {
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      await updateDoc(docRef, { status: "Draft", updatedAt: serverTimestamp() });
      setShowReopenConfirm(false);
    } finally {
      setReopening(false);
    }
  };

  // Save the edited submission number back to Firestore.
  const handleSaveSubNum = async () => {
    if (savingSubNum) return;
    setSavingSubNum(true);
    try {
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      const trimmed = subNumDraft.trim();
      await updateDoc(docRef, {
        submissionNumber: trimmed ? parseInt(trimmed, 10) : null,
        updatedAt: serverTimestamp(),
      });
    } finally {
      setSavingSubNum(false);
    }
  };

  // Change the credited co-creator PID (Draft, pre-submission only). Validates
  // the PID, updates secondCreatorPid, and reconciles the auto-share: the old
  // co-creator's invite/share is removed and the new one is invited. partnerPids
  // (accepted partners) are left untouched — those are managed via Add Partner.
  const handleSaveCoCreator = async () => {
    if (savingCoCreator) return;
    setSavingCoCreator(true);
    setCoCreatorError("");
    try {
      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      const raw = coCreatorDraft.trim();
      const oldSecond = session.secondCreatorPid || null;

      // Cleared — remove the co-creator and their auto-share.
      if (!raw) {
        const updates = { secondCreatorPid: null, updatedAt: serverTimestamp() };
        if (oldSecond) {
          updates.sharedWith = arrayRemove(oldSecond);
          updates.pendingPartnerPids = arrayRemove(oldSecond);
        }
        await updateDoc(docRef, updates);
        return;
      }

      const stripped = stripPidPrefix(raw);
      if (stripped === stripPidPrefix(session.creatorPid)) {
        setCoCreatorError("That's the session creator.");
        return;
      }
      const validatePID = httpsCallable(functions, "validatePID");
      const { data } = await validatePID({ pid: stripped });
      const resolved = data.pid;

      // Swap the credit and move the auto-share from old → new co-creator.
      await updateDoc(docRef, {
        secondCreatorPid: resolved,
        ...(oldSecond && oldSecond !== resolved
          ? {
              sharedWith: arrayRemove(oldSecond),
              pendingPartnerPids: arrayRemove(oldSecond),
            }
          : {}),
        updatedAt: serverTimestamp(),
      });
      // Invite the new co-creator (unless they're already an accepted partner).
      if (!(session.partnerPids || []).includes(resolved)) {
        await updateDoc(docRef, {
          sharedWith: arrayUnion(resolved),
          pendingPartnerPids: arrayUnion(resolved),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (err) {
      setCoCreatorError(err.message || "PID not found.");
    } finally {
      setSavingCoCreator(false);
    }
  };

  // Live preview of what the TBD submission ID will be when this session
  // is submitted — mirrors how submitToTBD composes it server-side.
  const previewCreator2 = session.partnerPids?.[0] || session.secondCreatorPid || session.creatorPid;
  const submissionPreview = `${projectCode(session.project)}_P${stripPidPrefix(session.creatorPid)}_P${stripPidPrefix(previewCreator2)}_${subNumDraft || "<auto>"}`;
  const currentSavedNum = session.submissionNumber != null ? String(session.submissionNumber) : "";
  const subNumDirty = subNumDraft !== currentSavedNum;
  const currentSavedCoCreator = session.secondCreatorPid ? String(session.secondCreatorPid) : "";
  const coCreatorDirty = coCreatorDraft.trim() !== currentSavedCoCreator;

  return (
    <div style={{ maxWidth: "1060px" }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", marginBottom: "14px" }}>
          <div>
            <h1 style={{ fontSize: "26px", fontWeight: "700", color: C.text1, letterSpacing: "-0.03em", marginBottom: "6px" }}>
              {session.title}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <span
                title={session.sessionId}
                style={{ fontSize: "12px", color: C.text3, fontFamily: "monospace", letterSpacing: "0.03em" }}
              >
                {/* Submission ID preview (replaces internal SESS-XXX id; */}
                {/* hover for the underlying Firestore session id) */}
                {`${projectCode(session.project)}_P${stripPidPrefix(session.creatorPid)}_P${stripPidPrefix(previewCreator2)}_${session.submissionNumber != null ? session.submissionNumber : "auto"}`}
              </span>
              <span style={{ color: "var(--border-2)" }}>·</span>
              <span style={{ fontSize: "12px", color: C.text3 }}>{session.project}</span>
              {allPartnerPids.length > 0 && (
                <>
                  <span style={{ color: "var(--border-2)" }}>·</span>
                  <span style={{ fontSize: "12px", color: "#a78bfa" }}>
                    {[
                      ...acceptedPartnerPids.map(p => `PID ${p}`),
                      ...pendingPartnerPids.map(p => `PID ${p} (invited)`),
                    ].join(", ")}
                  </span>
                </>
              )}
              {!viewingAs && isCreator && <AddPartnerBar session={session} />}
            </div>

            {/* Submission ID — editable only for a never-submitted Draft.
                Once a TBD task exists (incl. after reopening), it's locked so
                re-submit reuses the same task ID rather than forking a new one. */}
            {(isCreator && session.status === "Draft" && !session.tbdTaskId && !viewingAs) ? (
              <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "11px", fontWeight: "600", color: C.text3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Submission #
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="auto"
                  value={subNumDraft}
                  onChange={(e) => setSubNumDraft(e.target.value.replace(/[^0-9]/g, ""))}
                  onKeyDown={(e) => { if (e.key === "Enter" && subNumDirty) handleSaveSubNum(); }}
                  disabled={savingSubNum}
                  style={{
                    width: "90px", padding: "5px 9px",
                    background: "var(--surface-2)",
                    border: `1px solid ${subNumDirty ? "rgba(99,102,241,0.45)" : "var(--border)"}`,
                    borderRadius: "6px", color: C.text1,
                    fontSize: "12px", fontFamily: "monospace",
                    outline: "none",
                  }}
                />
                {subNumDirty && (
                  <button
                    onClick={handleSaveSubNum}
                    disabled={savingSubNum}
                    style={{
                      padding: "4px 10px", borderRadius: "6px",
                      background: "rgba(99,102,241,0.15)",
                      border: "1px solid rgba(99,102,241,0.4)",
                      color: "#a5b4fc", fontSize: "12px", fontWeight: "600",
                      cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {savingSubNum ? "Saving…" : "Save"}
                  </button>
                )}
                <span style={{ fontSize: "12px", color: C.text3 }}>·</span>
                <span style={{ fontSize: "12px", color: C.text3 }}>Preview:</span>
                <span style={{ fontSize: "12px", color: C.text2, fontFamily: "monospace", background: "var(--surface-2)", padding: "2px 7px", borderRadius: "4px" }}>
                  {submissionPreview}
                </span>
              </div>
            ) : session.tbdTaskId ? (
              <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "11px", fontWeight: "600", color: C.text3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Submission ID
                </span>
                <span style={{ fontSize: "12px", color: C.text2, fontFamily: "monospace", background: "var(--surface-2)", padding: "2px 7px", borderRadius: "4px" }}>
                  {session.tbdTaskId}
                </span>
              </div>
            ) : null}

            {/* Co-creator PID — editable for a never-submitted Draft only, so
                the credit flows correctly into the DMS task ID at submit. Once
                a task exists it's locked (the task ID can't be renamed). */}
            {(isCreator && session.status === "Draft" && !session.tbdTaskId && !viewingAs) && (
              <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "11px", fontWeight: "600", color: C.text3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Co-creator PID
                </span>
                <input
                  type="text"
                  placeholder="none"
                  value={coCreatorDraft}
                  onChange={(e) => { setCoCreatorDraft(e.target.value); setCoCreatorError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && coCreatorDirty) handleSaveCoCreator(); }}
                  disabled={savingCoCreator}
                  style={{
                    width: "90px", padding: "5px 9px",
                    background: "var(--surface-2)",
                    border: `1px solid ${coCreatorDirty ? "rgba(99,102,241,0.45)" : "var(--border)"}`,
                    borderRadius: "6px", color: C.text1,
                    fontSize: "12px", fontFamily: "monospace",
                    outline: "none",
                  }}
                />
                {coCreatorDirty && (
                  <button
                    onClick={handleSaveCoCreator}
                    disabled={savingCoCreator}
                    style={{
                      padding: "4px 10px", borderRadius: "6px",
                      background: "rgba(99,102,241,0.15)",
                      border: "1px solid rgba(99,102,241,0.4)",
                      color: "#a5b4fc", fontSize: "12px", fontWeight: "600",
                      cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {savingCoCreator ? "Saving…" : "Save"}
                  </button>
                )}
                {coCreatorError
                  ? <span style={{ fontSize: "11px", color: C.danger }}>{coCreatorError}</span>
                  : <span style={{ fontSize: "11px", color: C.text3 }}>Credited as second creator; they're invited to the session.</span>
                }
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            {/* Submit for QA — only while Draft; sits to the left of the
                status badge so it reads as "act → status". */}
            {canSubmit && session.status === "Draft" && (
              <div
                style={{ position: "relative" }}
                onMouseEnter={() => !canSubmitQA && setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <Btn
                  variant="primary"
                  onClick={canSubmitQA ? handleSubmitForQA : undefined}
                  disabled={!canSubmitQA}
                  loading={submitting}
                  size="sm"
                  style={{ padding: "8px 16px", fontSize: "13px" }}
                >
                  {submitting ? "Submitting…" : session.tbdTaskId ? "Re-submit for QA" : "Submit for QA"}
                </Btn>

                {showTooltip && !canSubmitQA && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 8px)", right: 0,
                    background: "var(--card)", border: "1px solid var(--border-2)",
                    borderRadius: "9px", padding: "12px 14px",
                    boxShadow: "0 16px 48px rgba(0,0,0,0.55)", zIndex: 30, minWidth: "200px",
                  }}>
                    <div style={{ fontSize: "12px", fontWeight: "600", color: C.text1, marginBottom: "6px" }}>
                      Missing required files:
                    </div>
                    {missingRequired.map(s => (
                      <div key={s} style={{ fontSize: "12px", color: C.warning, marginBottom: "3px" }}>· {s}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Reopen to edit — submitted folders that aren't yet Approved.
                Pulls the folder back to Draft so files can be added/replaced. */}
            {canReopen && (
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setShowReopenConfirm(s => !s)}
                  style={{
                    padding: "8px 16px", borderRadius: "8px",
                    background: "rgba(245,158,11,0.1)",
                    border: "1px solid rgba(245,158,11,0.25)",
                    color: C.warning, fontSize: "13px", fontWeight: "600",
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  Reopen to edit
                </button>

                {showReopenConfirm && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 8px)", right: 0,
                    background: "var(--card)", border: "1px solid var(--border-2)",
                    borderRadius: "10px", padding: "14px",
                    boxShadow: "0 16px 48px rgba(0,0,0,0.55)", zIndex: 40, width: "260px",
                  }}>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: C.text1, marginBottom: "6px" }}>
                      Reopen this folder?
                    </div>
                    <div style={{ fontSize: "12px", color: C.text3, lineHeight: "1.6", marginBottom: "12px" }}>
                      It goes back to <strong style={{ color: C.text2 }}>Draft</strong> so you can add or replace files. The submission ID stays the same — re-submit when you're done and QA picks up the same folder.
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => setShowReopenConfirm(false)}
                        style={{ flex: 1, padding: "7px", background: "transparent", border: "1px solid var(--border-2)", borderRadius: "7px", color: C.text2, fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleReopen}
                        disabled={reopening}
                        style={{ flex: 1, padding: "7px", background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: "7px", color: C.warning, fontSize: "12px", fontWeight: "600", cursor: reopening ? "not-allowed" : "pointer", fontFamily: "inherit" }}
                      >
                        {reopening ? "Reopening…" : "Reopen"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <span style={{
              fontSize: "11px", fontWeight: "700", letterSpacing: "0.06em", textTransform: "uppercase",
              padding: "5px 12px", borderRadius: "6px", whiteSpace: "nowrap",
              color: ss.color, background: ss.bg, border: `1px solid ${ss.border}`,
            }}>
              {session.status === "QA_Ready" ? "QA Ready" : session.status}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ flex: 1, height: "5px", background: "var(--border-subtle)", borderRadius: "999px", overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${(requiredFilled.length / REQUIRED_SLOTS.length) * 100}%`,
              background: allRequiredFilled ? C.success : C.primary,
              borderRadius: "999px",
              transition: "width 0.4s ease",
            }} />
          </div>
          <span style={{ fontSize: "12px", fontWeight: "600", color: allRequiredFilled ? C.success : C.text3, flexShrink: 0 }}>
            {requiredFilled.length}/{REQUIRED_SLOTS.length} required
          </span>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>

        {/* ── Left: files ── */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* View toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600", color: C.text3, textTransform: "uppercase", letterSpacing: "0.08em" }}>Files</span>
            <div style={{ display: "flex", background: "var(--surface-2)", borderRadius: "7px", padding: "2px", gap: "2px" }}>
              {[["slots", "Slot Cards"], ["table", "File Table"]].map(([mode, label]) => (
                <button key={mode} onClick={() => setViewMode(mode)} style={{ padding: "4px 12px", borderRadius: "5px", border: "none", background: viewMode === mode ? "rgba(99,102,241,0.18)" : "transparent", color: viewMode === mode ? "var(--og-accent)" : C.text3, fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {viewMode === "slots" ? (
            <div style={{ marginBottom: "28px" }}>
              <div style={{ display: "grid", gap: "10px", marginBottom: "20px" }}>
                {[...ALL_SLOTS.filter(s => s !== "Unlabeled"), ...Object.keys(session.slots || {}).filter(s => !ALL_SLOTS.includes(s) && s !== "Unlabeled")].map(slotName => (
                  <SlotCard
                    key={slotName}
                    slotName={slotName}
                    slotData={session.slots?.[slotName]}
                    isRequired={REQUIRED_SLOTS.includes(slotName)}
                    session={session}
                    pid={pid}
                    userName={userName}
                    readOnly={!canEdit}
                  />
                ))}
              </div>
              <UnlabeledSection session={session} readOnly={!canEdit} />
              {canEdit && (
                <div style={{ marginTop: "20px" }}>
                  <div style={{ fontSize: "11px", fontWeight: "600", color: C.text3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "10px" }}>Bulk Upload</div>
                  <BulkDropZone session={session} pid={pid} userName={userName} />
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginBottom: "28px" }}>
              <FileTable session={session} readOnly={!canEdit} />
              {canEdit && (
                <div style={{ marginTop: "24px" }}>
                  <div style={{ fontSize: "11px", fontWeight: "600", color: C.text3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "10px" }}>Bulk Upload</div>
                  <BulkDropZone session={session} pid={pid} userName={userName} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right: Requirements sidebar ── */}
        <div style={{ width: "210px", flexShrink: 0, position: "sticky", top: "24px" }}>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px" }}>

            <div style={{ fontSize: "11px", fontWeight: "700", color: C.text3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "14px" }}>
              Requirements
            </div>

            {/* Required slots */}
            <div style={{ marginBottom: "14px" }}>
              {REQUIRED_SLOTS.map(s => {
                const filled = !!session.slots?.[s]?.activeFile;
                return (
                  <div key={s} style={{ display: "flex", alignItems: "center", gap: "9px", padding: "5px 0", borderBottom: "1px solid var(--surface-2)" }}>
                    <span style={{ fontSize: "13px", color: filled ? C.success : "var(--border-2)", flexShrink: 0, lineHeight: 1 }}>
                      {filled ? "✓" : "○"}
                    </span>
                    <span style={{ fontSize: "12px", color: filled ? C.text1 : C.text3, fontWeight: filled ? "600" : "400" }}>
                      {s}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Divider + Optional */}
            <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: "12px", marginBottom: "4px" }}>
              <div style={{ fontSize: "10px", fontWeight: "700", color: C.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "8px" }}>
                Optional
              </div>
              {["Metadata JSON", "Unlabeled"].map(s => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: "9px", padding: "4px 0" }}>
                  <span style={{ fontSize: "11px", color: "var(--border-2)", flexShrink: 0 }}>○</span>
                  <span style={{ fontSize: "12px", color: C.text3 }}>{s}</span>
                </div>
              ))}
            </div>

          </div>
        </div>

      </div>{/* end two-column */}

      {/* ── Footer ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 20px",
        background: "rgba(255,255,255,0.02)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "12px",
      }}>
        <button
          onClick={onBack}
          style={{ background: "transparent", border: "none", color: "#34d399", fontSize: "13px", cursor: "pointer", fontFamily: "inherit", padding: 0, fontWeight: "500" }}
        >
          ← Dashboard
        </button>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {/* Delete — creator only, Draft only. Opens the confirm modal in App.js */}
          {!viewingAs && isCreator && session.status === "Draft" && onRequestDelete && (
            <button
              onClick={() => onRequestDelete(session)}
              style={{
                padding: "9px 16px", borderRadius: "8px",
                background: "var(--danger-btn-bg)",
                border: "1px solid var(--danger-btn-border)",
                color: "var(--danger-btn-color)", fontSize: "13px", fontWeight: "600",
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SessionWorkspace;
